import type { Feature, MultiPolygon, Polygon } from 'geojson';
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { createAppSlice } from '.';
import {
  createPartnerWorkRecord as createPartnerWorkRecordApi,
  getPartnerFields,
  getSyncedPartnerFarms,
  partnerFarmsSync,
  partnerFieldsSync,
  updatePartnerFarm as updatePartnerFarmApi,
  updatePartnerField as updatePartnerFieldApi,
} from '../api/api';
import { PARTNERS_CONSTANTS } from '../constants/partnersConstants';
import { I18n } from '../i18n';
import { ClusterId } from './clusters';
import { ParcelId } from './parcels';
import { EquipmentSessionId, fetchEquipmentSession } from './sessions';
import { alerting, errorHasOccurred, snackAdded, success } from './snacks';
import { userLoggedOut } from './users';

type PartnerName = typeof PARTNERS_CONSTANTS.SMAG | typeof PARTNERS_CONSTANTS.MESPARCELLES;
type PartnerFarmId = number;
type PartnerFieldId = number;

type FieldGeometry = string | Feature<Polygon | MultiPolygon>;

type PartnerFarm = {
  id: PartnerFarmId;
  name: string;
  karnott_cluster_id?: ClusterId;
  partner_metadata: Record<string, string>;
};

type PartnerFieldErrorStatus = 'INTERSECTION' | 'NO_SHAPE' | 'INTERSECTION_WITH_ANOTHER_CLUSTER_PARCEL_ERROR';

type PartnerField = {
  id: PartnerFieldId;
  name: string;
  year: number;
  farm_id: PartnerFarmId;
  error_status?: PartnerFieldErrorStatus;
  geometry_area?: FieldGeometry;
  karnott_parcel_id?: ParcelId;
  karnott_parcels?: { id: ParcelId; geometry_area?: FieldGeometry }[];
};

type PartnerState = {
  farms: Record<PartnerFarmId, PartnerFarm>;
  fields: Record<PartnerFieldId, PartnerField>;
};

type State = Record<PartnerName, PartnerState>;

type Dispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

type ApiError = { errors?: { reason?: string }[] };

const initialState: State = {
  [PARTNERS_CONSTANTS.SMAG]: { farms: {}, fields: {} },
  [PARTNERS_CONSTANTS.MESPARCELLES]: { farms: {}, fields: {} },
};

function byId<T extends { id: number }>(items: T[]): Record<number, T> {
  return Object.fromEntries(items.map((item) => [item.id, item]));
}

async function alertingUnless<T>(
  call: () => Promise<T>,
  handled: { reason: string; message: string },
  dispatch: Dispatch,
): Promise<T> {
  try {
    return await call();
  } catch (e) {
    if ((e as ApiError)?.errors?.[0]?.reason === handled.reason) {
      dispatch(snackAdded({ type: 'error', duration: 5000, message: handled.message, action: 'OK' }));
      throw e;
    }
    return errorHasOccurred(e, dispatch);
  }
}

function workRecordErrorMessage(e: unknown): string | undefined {
  return ((e as ApiError)?.errors || [])
    .map((error) => {
      switch (error?.reason) {
        case 'noParcelSessions':
          return I18n.t('EquipmentSessionDetails.create_partner_work_record.errors.no_parcel_error', {
            partner_name: 'SMAG',
          });
        case 'noEquipmentModel':
          return I18n.t('EquipmentSessionDetails.create_partner_work_record.errors.no_equipment_model', {
            partner_name: 'SMAG',
          });
        case 'partnerOperationTypeNotFound':
        case 'partnerOperationTypeUidNotFound':
          return I18n.t('EquipmentSessionDetails.create_partner_work_record.errors.operation_type_not_found', {
            partner_name: 'SMAG',
          });
        case 'someParcelsNotLinkedToPartnerField':
          return I18n.t('EquipmentSessionDetails.create_partner_work_record.errors.some_parcels_not_linked_to_smag', {
            partner_name: 'SMAG',
          });
        case 'createParcelSessionWorkRecordError':
          return I18n.t('EquipmentSessionDetails.create_partner_work_record.errors.smag_api_error', {
            partner_name: 'SMAG',
          });
        case 'freezeEquipmentSessionError':
          return I18n.t('EquipmentSessionDetails.create_partner_work_record.errors.error_to_freeze_equipment_session', {
            partner_name: 'SMAG',
          });
        default:
          return undefined;
      }
    })
    .find((message) => message);
}

export function withParsedGeometries(field: PartnerField): PartnerField {
  const parsed = { ...field };
  if (typeof parsed.geometry_area === 'string') {
    parsed.geometry_area = JSON.parse(parsed.geometry_area) as FieldGeometry;
  }
  if (parsed.karnott_parcels?.length) {
    parsed.karnott_parcels = parsed.karnott_parcels.map((parcel) =>
      typeof parcel.geometry_area === 'string'
        ? { ...parcel, geometry_area: JSON.parse(parcel.geometry_area) as FieldGeometry }
        : parcel,
    );
  }
  return parsed;
}

const partnerSlice = createAppSlice({
  name: 'partner',
  initialState,
  reducers: (create) => ({
    syncPartnerFarms: create.asyncThunk(
      async ({ partnerName, cropYear }: { partnerName: PartnerName; cropYear?: number }, { dispatch }) => {
        const farms = (await alerting(() => partnerFarmsSync(partnerName, cropYear), { dispatch })) as
          | PartnerFarm[]
          | null;
        return { partnerName, farms: farms || [] };
      },
      {
        fulfilled: (state, { payload }) => {
          payload.farms.forEach((farm) => {
            state[payload.partnerName].farms[farm.id] = farm;
          });
        },
      },
    ),
    fetchPartnerFarms: create.asyncThunk(
      async (partnerName: PartnerName, { dispatch }) => {
        const farms = (await alerting(() => getSyncedPartnerFarms(partnerName), { dispatch })) as PartnerFarm[] | null;
        return { partnerName, farms: farms || [] };
      },
      {
        fulfilled: (state, { payload }) => {
          state[payload.partnerName].farms = byId(payload.farms);
        },
      },
    ),
    updatePartnerFarm: create.asyncThunk(
      async (
        {
          partnerName,
          id,
          karnottClusterID,
        }: { partnerName: PartnerName; id: PartnerFarmId; karnottClusterID: ClusterId | null },
        { dispatch },
      ) => {
        const farm = (await alertingUnless(
          () => updatePartnerFarmApi(partnerName, { id, karnottClusterID }),
          {
            reason: 'fieldsFarmAreAlreadySyncedWithKarnottFields',
            message: I18n.t('Api.errors.fieldsFarmAreAlreadySyncedWithKarnottFields', {
              partner_name: partnerName.toUpperCase(),
            }),
          },
          dispatch,
        )) as PartnerFarm;
        success('PartnerClusters.link_ok', { dispatch, duration: 5000 });
        return { partnerName, farm };
      },
      {
        fulfilled: (state, { payload }) => {
          state[payload.partnerName].farms[payload.farm.id] = payload.farm;
        },
      },
    ),
    syncPartnerFields: create.asyncThunk(
      async ({ partnerName, cropYear }: { partnerName: PartnerName; cropYear?: number }, { dispatch }) => {
        const fields = (await alerting(() => partnerFieldsSync(partnerName, cropYear), { dispatch })) as
          | PartnerField[]
          | null;
        return { partnerName, fields: fields || [] };
      },
      {
        fulfilled: (state, { payload }) => {
          state[payload.partnerName].fields = byId(payload.fields);
        },
      },
    ),
    fetchPartnerFields: create.asyncThunk(
      async ({ partnerName, cropYear }: { partnerName: PartnerName; cropYear?: number }, { dispatch }) => {
        const fields = (await alerting(() => getPartnerFields(partnerName, cropYear), { dispatch })) as
          | PartnerField[]
          | null;
        return { partnerName, fields: fields || [] };
      },
      {
        fulfilled: (state, { payload }) => {
          state[payload.partnerName].fields = byId(payload.fields);
        },
      },
    ),
    updatePartnerField: create.asyncThunk(
      async (
        {
          partnerName,
          id,
          karnottParcelID,
        }: { partnerName: PartnerName; id: PartnerFieldId; karnottParcelID: ParcelId | null },
        { dispatch },
      ) => {
        const field = (await alertingUnless(
          () => updatePartnerFieldApi(partnerName, { id, karnottParcelID }),
          {
            reason: 'alreadyLinkToAnotherFieldError',
            message: I18n.t('Api.errors.alreadyLinkToAnotherFieldError'),
          },
          dispatch,
        )) as PartnerField;
        return { partnerName, field };
      },
      {
        fulfilled: (state, { payload }) => {
          state[payload.partnerName].fields[payload.field.id] = payload.field;
        },
      },
    ),
    createPartnerWorkRecord: create.asyncThunk(
      async (
        { equipmentSessionId, year }: { equipmentSessionId: EquipmentSessionId; year: number },
        { dispatch },
      ): Promise<void> => {
        try {
          await createPartnerWorkRecordApi({ equipmentSessionId, year });
        } catch (e) {
          const message = workRecordErrorMessage(e);
          if (!message) {
            return errorHasOccurred(e, dispatch);
          }
          dispatch(snackAdded({ type: 'error', duration: 5000, message, action: 'OK' }));
          return;
        }
        // the session is frozen by the work record creation, so it has to be read back
        void dispatch(fetchEquipmentSession(equipmentSessionId));
        success('EquipmentSessionDetails.create_partner_work_record_success', {
          dispatch,
          duration: 5000,
          params: { partner_name: 'SMAG' },
        });
      },
    ),
  }),
  selectors: {
    selectPartnerFarmsById: (state, partnerName: PartnerName) => state[partnerName].farms,
    selectPartnerFieldsById: (state, partnerName: PartnerName) => state[partnerName].fields,
  },
  extraReducers: (builder) => {
    builder.addCase(userLoggedOut, () => initialState);
  },
});

export const { selectPartnerFarmsById, selectPartnerFieldsById } = partnerSlice.selectors;
export const {
  syncPartnerFarms,
  fetchPartnerFarms,
  updatePartnerFarm,
  syncPartnerFields,
  fetchPartnerFields,
  updatePartnerField,
  createPartnerWorkRecord,
} = partnerSlice.actions;
export default partnerSlice.reducer;
